/**
 * Pull per-field validation errors out of an IRIS / API envelope so the
 * entity form can highlight the inputs instead of toasting a dump.
 */

const SKIP_KEYS = new Set([
  'status',
  'status_code',
  'code',
  'request_id',
  'message',
  'detail',
  'non_field_errors',
  'errors',
])

function firstMessage(raw: unknown): string | null {
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (Array.isArray(raw) && raw.length > 0) return firstMessage(raw[0])
  return null
}

function fromRecord(record: Record<string, unknown>): Record<string, string> {
  const nested = record.attribute_values
  const source =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : record
  const out: Record<string, string> = {}
  for (const [key, raw] of Object.entries(source)) {
    if (SKIP_KEYS.has(key) || key === 'attribute_values') continue
    const message = firstMessage(raw)
    if (message) out[key] = message
  }
  return out
}

export function fieldErrorsFromApi(err: unknown): Record<string, string> {
  if (!err || typeof err !== 'object') return {}
  const response = (err as { response?: { data?: unknown } }).response
  const payload = response?.data ?? err
  if (!payload || typeof payload !== 'object') return {}
  const record = payload as Record<string, unknown>
  const result = record.result
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return fromRecord(result as Record<string, unknown>)
  }
  return fromRecord(record)
}

export function scrollToEntityField(codeName: string): void {
  const root = document.querySelector('[data-testid="iris-entity-form"]')
  // Inputs carry the question code as their id; some widgets set a name.
  const target =
    root?.querySelector<HTMLElement>(`[name="${codeName}"]`) ??
    root?.querySelector<HTMLElement>(`#${CSS.escape(codeName)}`) ??
    (root as HTMLElement | null)
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (target && 'focus' in target && typeof target.focus === 'function') {
    target.focus()
  }
}
